import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { BookmarkService } from '../../services/bookmark.service';
import { ConfigService } from '@web-client/shared';

@Component({
  selector: 'lib-bookmark-dialog',
  standalone: true,
  imports: [CommonModule, MatDialogModule, MatButtonModule, MatIconModule],
  template: `
    <div class="p-0 overflow-hidden rounded-3xl max-w-lg" style="background-color: var(--mat-sys-surface); color: var(--mat-sys-on-surface)">
      <!-- Header Image/Pattern -->
      <div class="h-32 relative overflow-hidden flex items-center justify-center" style="background-color: var(--mat-sys-primary)">
        <mat-icon class="!w-16 !h-16 !text-[64px]" style="color: var(--mat-sys-on-primary)">folder_special</mat-icon>
      </div>

      <div class="p-8">
        <h2 class="text-2xl font-bold mb-2">Master Your Workflow</h2>
        <p class="mb-8 opacity-70">
          Keep all {{appName}} tools at your fingertips by creating a structured folder in your browser's bookmark bar.
        </p>

        <div class="space-y-6 mb-10">
          <div class="flex items-start space-x-4">
            <div class="w-8 h-8 rounded-full flex items-center justify-center font-bold flex-shrink-0" 
                 style="background-color: var(--mat-sys-primary-container); color: var(--mat-sys-on-primary-container)">1</div>
            <div>
              <h4 class="font-bold text-sm">Download the Pack</h4>
              <p class="text-xs opacity-60 mt-1">Click the button below to get your organized <code>{{appName}}-Bookmarks.html</code> file.</p>
            </div>
          </div>

          <div class="flex items-start space-x-4">
            <div class="w-8 h-8 rounded-full flex items-center justify-center font-bold flex-shrink-0"
                 style="background-color: var(--mat-sys-primary-container); color: var(--mat-sys-on-primary-container)">2</div>
            <div>
              <h4 class="font-bold text-sm">Import to Browser</h4>
              <p class="text-xs opacity-60 mt-1">Open <b>Bookmark Manager</b> (<code>Ctrl+Shift+O</code>) and select <b>"Import Bookmarks"</b>.</p>
            </div>
          </div>

          <div class="flex items-start space-x-4">
            <div class="w-8 h-8 rounded-full flex items-center justify-center font-bold flex-shrink-0"
                 style="background-color: var(--mat-sys-primary-container); color: var(--mat-sys-on-primary-container)">3</div>
            <div>
              <h4 class="font-bold text-sm">Instant Access</h4>
              <p class="text-xs opacity-60 mt-1">A clean "{{appName}}" folder will appear on your bar with categorized utilities.</p>
            </div>
          </div>
        </div>

        <div class="flex gap-3">
          <button mat-flat-button color="primary" (click)="downloadAndClose()" class="flex-1 !py-6 !rounded-xl !font-bold">
            <mat-icon class="mr-2">download</mat-icon>
            Download Bookmark Pack
          </button>
          <button mat-stroked-button mat-dialog-close class="!py-6 !rounded-xl">Cancel</button>
        </div>
      </div>
    </div>
  `,
  styles: [`
    :host { display: block; }
    ::ng-deep .mat-mdc-dialog-container .mdc-dialog__surface {
        border-radius: 24px !important;
        padding: 0 !important;
    }
  `]
})
export class BookmarkDialogComponent {
  private readonly configService = inject(ConfigService);
  get appName(): string {
    return this.configService.appName;
  }

  private readonly dialogRef = inject(MatDialogRef<BookmarkDialogComponent>);
  private readonly bookmarkService = inject(BookmarkService);

  downloadAndClose() {
    this.bookmarkService.downloadBookmarkPack();
    this.dialogRef.close();
  }
}
